import { useState } from 'react';
import { createPortal } from 'react-dom';
import './Liabilityagreement.css';

// ── MODAL ─────────────────────────────────────────────────────────────────────

function LiabilityModal({ role, onClose }) {
  const isTransporter = role === 'transporter';

  return createPortal(
    <div className="lia-backdrop" onClick={onClose}>
      <div className="lia-modal" onClick={e => e.stopPropagation()}>
        <div className="lia-header">
          <h2>Liability & Legal Disclaimer</h2>
          <button className="lia-close" onClick={onClose}>✕</button>
        </div>

        <div className="lia-body">

          {/* 1 — Platform role */}
          <section className="lia-section">
            <h3>1. Transportily is an Intermediary Only</h3>
            <p>
              SBH The Company (operating Transportily) operates exclusively as a digital marketplace connecting independent
              transporters and senders. Transportily is <strong>not a party to any transport contract</strong>,
              does not take possession of any goods, and does not act as a carrier, freight forwarder,
              or logistics provider. All transport agreements are entered into solely between the
              transporter and the sender.
            </p>
            <p>
              Transportily's role is limited to providing the technical platform that enables users
              to find each other and coordinate transport. Transportily accepts no responsibility for
              the condition, loss, theft, seizure, damage, or delay of any item transported through
              arrangements made on this platform.
            </p>
          </section>

          {/* 2 — No insurance */}
          <section className="lia-section">
            <h3>2. No Insurance Provided</h3>
            <p>
              Transportily does <strong>not provide any insurance</strong> — cargo, transit, liability,
              or otherwise — for items carried through this platform. Neither transporters nor senders
              are covered by any Transportily policy in the event of loss, damage, theft, or customs
              seizure. Users are solely responsible for arranging their own insurance if desired.
            </p>
          </section>

          {/* 3 — Customs law — critical and specific */}
          <section className="lia-section">
            <h3>3. Customs & Legal Compliance — Germany / EU</h3>
            <p>
              The transport of goods between Tunisia and Germany crosses an international customs
              border. Both transporters and senders are personally and exclusively responsible for
              compliance with all applicable customs, import, export, and tax laws, including but not
              limited to:
            </p>
            <ul className="lia-list">
              <li>
                <strong>Duty-free thresholds:</strong> Goods entering Germany from Tunisia (a non-EU
                country) are subject to EU customs rules. Items brought in personal luggage are
                duty-free only up to <strong>€430</strong> per traveller (air/sea) or <strong>€300</strong>
                {' '}(land travel), provided they are for personal use and not of a commercial nature
                (§ Einreise-Freimengen-Verordnung; EU Regulation 1186/2009).
              </li>
              <li>
                <strong>Commercial goods:</strong> Goods carried on behalf of another person for
                compensation do not qualify as personal-use items under German customs law. Transporters
                carrying items for senders may be viewed by customs authorities as conducting a
                commercial transport activity. Duties, VAT (19%), and penalties may apply.
              </li>
              <li>
                <strong>Prohibited & restricted items:</strong> The import into Germany of certain
                goods is prohibited or restricted, including narcotics, counterfeit goods, certain
                foodstuffs, weapons, and items exceeding phytosanitary or CITES limits. A full list
                is published by the German Customs Authority (Zoll):
                {' '}<a href="https://www.zoll.de/EN/Private-individuals/Travel/Entering-Germany/Restrictions/restrictions_node.html" target="_blank" rel="noopener noreferrer">www.zoll.de</a>.
              </li>
              <li>
                <strong>Cash & means of payment:</strong> Cash or equivalent payment instruments
                totalling €10,000 or more must be declared in writing at the German border (§ 12a
                ZollVG; EU Regulation 2018/1672).
              </li>
              <li>
                <strong>Criminal liability:</strong> Smuggling, customs fraud, and false declarations
                are criminal offences under § 370 AO (Abgabenordnung — German Tax Code) and can
                result in fines or imprisonment. Transporters bear personal criminal liability for
                goods found in their luggage.
              </li>
            </ul>
            <p>
              <strong>Transportily expressly disclaims all liability</strong> arising from customs
              violations, seizures, fines, or criminal proceedings related to goods transported
              through arrangements made on this platform.
            </p>
          </section>

          {/* 4 — Transporter responsibilities */}
          {(isTransporter || !isTransporter) && (
            <section className="lia-section">
              <h3>4. Transporter Responsibilities</h3>
              <ul className="lia-list">
                <li>You are solely responsible for all goods you physically carry across any border, regardless of who the goods belong to.</li>
                <li>You must verify the contents and declared value of all items before accepting them. Ignorance of actual contents is not a defence under German customs law.</li>
                <li>You are responsible for the safe custody of items from collection to delivery. Any loss, damage, or theft is your personal liability toward the sender.</li>
                <li>You must comply with airline, railway, or other carrier restrictions on weight, size, and prohibited items.</li>
                <li>You must not carry items whose import or export is prohibited or restricted without proper authorisation.</li>
              </ul>
            </section>
          )}

          {/* 5 — Sender responsibilities */}
          <section className="lia-section">
            <h3>5. Sender Responsibilities</h3>
            <ul className="lia-list">
              <li>You must provide a complete, accurate, and truthful description of all items you ask a transporter to carry.</li>
              <li>You must not submit items that are prohibited, restricted, or require import/export licences without disclosing this to the transporter and ensuring compliance.</li>
              <li>You are responsible for the declared value of your items. Underdeclaration is a customs offence.</li>
              <li>You accept that the transporter, not Transportily, is your contractual counterpart and bears physical responsibility for your items in transit.</li>
              <li>You acknowledge that if customs authorities seize your items or impose duties, Transportily bears no liability and will not compensate you.</li>
            </ul>
          </section>

          {/* 6 — Limitation of liability */}
          <section className="lia-section">
            <h3>6. Limitation of Transportily's Liability</h3>
            <p>
              To the fullest extent permitted by applicable law — including § 307 BGB (German Civil Code)
              on standard contract terms — Transportily excludes all liability for:
            </p>
            <ul className="lia-list">
              <li>Loss, damage, delay, or seizure of any item transported through the platform</li>
              <li>Customs duties, taxes, fines, or penalties imposed on users</li>
              <li>Criminal proceedings arising from transport arrangements made on the platform</li>
              <li>Disputes between transporters and senders arising from transport arrangements</li>
              <li>Inaccurate information provided by users to each other or to customs authorities</li>
            </ul>
            <p>
              Nothing in this disclaimer excludes liability for fraud, wilful misconduct, or death
              or personal injury caused by Transportily's own negligence, as required by mandatory
              German law.
            </p>
          </section>

          {/* 7 — Governing law */}
          <section className="lia-section">
            <h3>7. Governing Law</h3>
            <p>
              This disclaimer and all use of the Transportily platform are governed by the laws of
              the Federal Republic of Germany. Any disputes shall be subject to the exclusive
              jurisdiction of the courts of Germany, without prejudice to mandatory consumer
              protection provisions applicable in the user's country of residence.
            </p>
            <p style={{ marginTop: '10px', fontSize: '12px', color: 'rgba(245,237,216,0.45)' }}>
              Last updated: March 2026. This disclaimer does not constitute legal advice.
              Users are encouraged to consult a qualified lawyer regarding their specific obligations.
            </p>
          </section>

        </div>

        <div className="lia-footer">
          <button className="btn btn-primary" onClick={onClose}>I have read and understood this</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── CHECKBOX COMPONENT — drop this into PostTrip & booking forms ──────────────

export default function LiabilityAgreement({ role, agreed, onChange }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="lia-checkbox-wrap">
      <label className="lia-checkbox-label">
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => onChange(e.target.checked)}
          className="lia-checkbox-input"
        />
        <span className="lia-checkbox-text">
          I have read and agree to the{' '}
          <button
            type="button"
            className="lia-link-btn"
            onClick={e => { e.preventDefault(); setModalOpen(true); }}
          >
            Liability Disclaimer & Legal Terms
          </button>
          . I understand that Transportily is an intermediary only, that I am solely responsible
          for customs compliance, and that no insurance is provided.
        </span>
      </label>

      {modalOpen && (
        <LiabilityModal role={role} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}